'use strict';

import React, {
    useCallback,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';

import {__} from '~/shared/src/utilities/messageCatalog';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import closePanel from '~/frontend/src/actions/panelManager/closePanel';
import openPanel, {PanelPropsWithPanelType} from '~/frontend/src/actions/panelManager/openPanel';
import {getPanelIndex} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {
    FormPanelProps,
    OverviewPanelProps,
    TreeViewPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/definitions';


type DefaultProps = {
    labeled? : boolean;
    className? : string;
    inputStyle? : React.CSSProperties;
}
const defaultProps : DefaultProps = {
    labeled   : true,
    className : '',
    inputStyle: { width: '100%' }
};

type CommonProps = {
    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    currentView? : string;

    style? : React.CSSProperties;
};

type Props = CommonProps & (
    {
        schema? : never;
        itemId? : never;
        itemType? : never;
    } |
    {
        schema : Schema;
        itemId : number;
        itemType : number | string;
    }
    );

export type ComponentProps = Props & DefaultProps;
type AllProps = Props & Required<DefaultProps>;


const PanelSelectorDropdown = ( props : AllProps ) => {
    const {
              labeled,
              className,
              currentView,
              inputStyle,
              itemId,
              itemType,
              schema,
              style,
              uid
          }                      = props,
          dispatch : AppDispatch = useDispatch(),
          user                   = useSelector( ( state : StoreState ) => state.session?.user ),
          panelManager           = useSelector( ( state : StoreState ) => state.panelManager ),
          data                   = useMemo(
              () => {
                  const retVal = [];

                  if ( schema && user ) {
                      const panelProps = user.getPanelPropsForItemType( schema, itemType );

                      for ( let i = 0; i < panelProps.length; i++ ) {
                          if ( panelProps[ i ].panelType === 'Form' || panelProps[ i ].panelType === 'Overview' || panelProps[ i ].panelType === 'TreeView' ) {
                              const viewPanelProps = panelProps[ i ] as FormPanelProps | OverviewPanelProps | TreeViewPanelProps;

                              retVal.push( {
                                  id   : i,
                                  label: __(
                                      `YN.base.panelSelector.${viewPanelProps.panelType}`,
                                      {
                                          label: viewPanelProps.viewName
                                              ? `${viewPanelProps.viewName} (${viewPanelProps.viewKey})`
                                              : viewPanelProps.viewKey
                                      }
                                  ),
                                  value: `${viewPanelProps.panelType}:${viewPanelProps.viewKey}`,
                                  data : viewPanelProps
                              } );
                          }
                          else {
                              retVal.push( {
                                  id   : i,
                                  label: __( `YN.base.panelSelector.${panelProps[ i ].panelType}` ),
                                  value: `${panelProps[ i ].panelType}`,
                                  data : panelProps[ i ]
                              } );
                          }
                      }

                      retVal.sort( ( d1, d2 ) => {
                          if ( d1.label.toLowerCase() > d2.label.toLowerCase() ) {
                              return 1;
                          }
                          else if ( d2.label.toLowerCase() > d1.label.toLowerCase() ) {
                              return -1;
                          }
                          else {
                              return 0;
                          }
                      } );

                      // always add generic view if the user have the genericPermission for
                      if ( user.hasGenericPermissionOnItemType( 'read', itemType, schema ) ) {
                          retVal.push( {
                              id   : retVal.length,
                              label: __( 'YN.base.panelSelector.genericView' ),
                              value: 'generic',
                              data : {}
                          } );
                      }
                  }

                  return retVal;
              },
              [
                  itemType,
                  schema,
                  user
              ]
          ),
          onChange               = useCallback(
              ( event : DropdownChangeEvent ) => {
                  const doFnc = async () => {
                      if ( panelManager?.activePanelKey ) {
                          const panelIndex = getPanelIndex( panelManager.activePanelKey ),
                                panelProps = data.find( d => d.value === event.value )?.data as PanelPropsWithPanelType;

                          if ( panelProps ) {
                              await dispatch( closePanel( panelManager.activePanelKey ) );

                              if ( event.value === 'generic' ) {
                                  dispatch( openPanel(
                                      {
                                          panelType: 'FinderDetail',
                                          //
                                          schema,
                                          //
                                          rootItemId: itemId
                                      },
                                      undefined,
                                      undefined,
                                      undefined,
                                      panelIndex
                                  ) );
                              }
                              else {
                                  if (
                                      itemId
                                      && panelProps.panelType !== 'FinderOverview'
                                      && panelProps.panelType !== 'Overview'
                                      && panelProps.panelType !== 'QueryExecute'
                                      && panelProps.panelType !== 'TreeTest'
                                      && panelProps.panelType !== 'XmlImport'
                                  ) {

                                      dispatch( openPanel(
                                          {
                                              ...panelProps,
                                              schema,
                                              rootItemId: itemId
                                          },
                                          undefined,
                                          undefined,
                                          undefined,
                                          panelIndex
                                      ) );
                                  }
                                  else {
                                      dispatch( openPanel(
                                          {
                                              ...panelProps,
                                              schema: schema ?? 'DATA'
                                          },
                                          undefined,
                                          undefined,
                                          undefined,
                                          panelIndex
                                      ) );
                                  }
                              }
                          }
                      }
                  };

                  void doFnc();
              },
              [
                  data,
                  itemId,
                  itemType,
                  panelManager,
                  schema
              ]
          ),
          content                = useMemo(
              () => {
                  const dropdownClassNames = [ 'yn-inputgroup' ];

                  if ( !labeled ) {
                      dropdownClassNames.push( className );
                  }

                  return <div className={className + ' flex field'} style={style}>
                      {labeled
                          ? <label
                              htmlFor={`${uid}_panelSelectorDropdown_input`}
                              className="yn-field-label"
                          >
                              {__( 'YN.base.panelSelector.field.label' )}
                          </label>
                          : ''
                      }
                      <Dropdown
                          id={`${uid}_panelSelectorDropdown`}
                          inputId={`${uid}_panelSelectorDropdown_input`}
                          value={currentView}
                          onChange={onChange}
                          options={data}
                          dataKey="id"
                          optionLabel="label"
                          className={dropdownClassNames.join( ' ' )}
                          disabled={data.length < 2}
                          style={{ ...inputStyle, flex: '1 1 auto', width: '1px', display: 'inline-flex' }}
                      />
                  </div>;
              },
              [
                  className,
                  currentView,
                  data,
                  inputStyle,
                  labeled,
                  style,
                  uid,
                  user
              ]
          );

    return content;
};
PanelSelectorDropdown.defaultProps = defaultProps;
PanelSelectorDropdown.displayName = 'PanelSelectorDropdown';

export default PanelSelectorDropdown;
